import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { PersonAdd } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../../contexts/DeviceContext';
import FavouriteCharacterSection from '../FavouriteCharacter';
import ScheduleSettingsSection from '../ScheduleSettingsSection';
import ImpactDetectionSection from '../ImpactDetectionSection';
import DashLockCard from '../DashLockCard';
import AppVisibilitySection from '../AppVisibilitySection';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { selectedDevice, handleInviteParent } = useDevice();

  const [openDialog, setOpenDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'viewer' | 'editor'>('editor');

  if (!selectedDevice) return null;

  const handleInviteSubmit = async () => {
    await handleInviteParent(inviteEmail, inviteRole);
    setInviteEmail('');
    setOpenDialog(false);
  };

  return (
    <>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>{t('deviceSettings.title')}</Typography>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              {t('deviceSettings.deviceName', { name: selectedDevice.name })}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              {t('deviceSettings.deviceId', { id: selectedDevice.id })}
            </Typography>
            {selectedDevice.is_owner && (
              <Button
                variant="outlined"
                startIcon={<PersonAdd />}
                sx={{ mt: 2 }}
                onClick={() => setOpenDialog(true)}
              >
                {t('deviceSettings.inviteCoParent')}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Schedule Settings Section */}
      <ScheduleSettingsSection />

      {/* Impact Detection Section */}
      <ImpactDetectionSection />

      {/* How to leave pinned mode (BA-147) */}
      <DashLockCard />

      {/* App Visibility Section */}
      <AppVisibilitySection />

      {/* Favourite Character Section */}
      <FavouriteCharacterSection />

      {/* Invite Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t('invites.dialogTitle')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('invites.emailAddress')}
            type="email"
            fullWidth
            variant="outlined"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined">
            <InputLabel>{t('invites.role')}</InputLabel>
            <Select
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as 'viewer' | 'editor')}
              label={t('invites.role')}
            >
              <MenuItem value="viewer">{t('invites.viewerRole')}</MenuItem>
              <MenuItem value="editor">{t('invites.editorRole')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleInviteSubmit} variant="contained">{t('invites.sendInvite')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SettingsPage;
